// src/App.tsx
import type { ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { GuildProvider } from './lib/GuildContext';
import { AppShell } from './components/AppShell/AppShell';
import { Login } from './pages/Login';
import { Privacy } from './pages/Privacy';
import { Overview } from './pages/Overview';
import { CommandsPage } from './features/commands/CommandsPage';
import { PermissionsPage } from './features/permissions/PermissionsPage';
import { ChannelsPage } from './features/channels/ChannelsPage';
import { MarketAlertsPage } from './features/marketAlerts/MarketAlertsPage';
import { ClanEventsPage } from './features/clanEvents/ClanEventsPage';
import { ClanWatchesPage } from './features/clanWatches/ClanWatchesPage';
import { BotHealthPage } from './features/botHealth/BotHealthPage';
import { LogsPage } from './features/logs/LogsPage';
import { CreatorGate } from './features/ownerAdmin/CreatorGate';
import { CreatorOverviewPage } from './features/ownerAdmin/CreatorOverviewPage';
import { ServersPage } from './features/ownerAdmin/ServersPage';
import { AnnouncementsPage } from './features/ownerAdmin/AnnouncementsPage';
import { ConfigPage } from './features/ownerAdmin/ConfigPage';
import { CreatorReportsPage } from './features/ownerAdmin/CreatorReportsPage';
import { CreatorLogsPage } from './features/ownerAdmin/CreatorLogsPage';
import { CreatorAuditPage } from './features/ownerAdmin/CreatorAuditPage';
import { ScheduledReportsPage } from './features/scheduledReports/ScheduledReportsPage';
import { SettingsPage } from './features/settings/SettingsPage';

// Gate: redirects to /login if GET /me failed (not authenticated).
// Privacy stays reachable without auth — matches the existing server.js
// behavior where /privacy had no requireAuth.
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading, error } = useAuth();
  if (loading) return null; // TODO: real loading state once a spinner/skeleton component exists
  if (error || !user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />

          <Route
            path="/"
            element={
              <RequireAuth>
                <GuildProvider>
                  <AppShell />
                </GuildProvider>
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="/overview" replace />} />
            <Route path="overview" element={<Overview />} />
            <Route path="commands" element={<CommandsPage />} />
            <Route path="permissions" element={<PermissionsPage />} />
            <Route path="market-alerts" element={<MarketAlertsPage />} />
            <Route path="clan-events" element={<ClanEventsPage />} />
            <Route path="clan-watches" element={<ClanWatchesPage />} />
            <Route path="reports" element={<ScheduledReportsPage />} />
            <Route path="channels" element={<ChannelsPage />} />
            <Route path="logs" element={<LogsPage />} />
            <Route path="settings" element={<SettingsPage />} />

            {/* Creator-only section — CreatorGate checks isCreator +
                passcode unlock once, then every nested route renders
                via its Outlet. Bot Health moved here from the regular
                routes above (2026-07-14) - no longer visible to
                regular users at all. Owner Admin split from one
                monolithic page into four separate ones, since a
                single page holding stats + guild activity + reports +
                cross-guild logs + the creator audit trail together
                was getting noticeably laggy. */}
            <Route path="owner-admin" element={<CreatorGate />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<CreatorOverviewPage />} />
              <Route path="servers" element={<ServersPage />} />
              <Route path="reports" element={<CreatorReportsPage />} />
              <Route path="logs" element={<CreatorLogsPage />} />
              <Route path="creator-logs" element={<CreatorAuditPage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="config" element={<ConfigPage />} />
            </Route>
            <Route path="bot-health" element={<CreatorGate />}>
              <Route index element={<BotHealthPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
